import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Bullets from 'slickpay/common/Bullets.component';
import ImageCardGroup from 'slickpay/common/ImageCardGroup.component';
import Banner from 'slickpay/no-reader/Banner.component';
import FaqTips from 'timeOffReports/components/FaqTips.component';

import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import IMG_ADMIN from 'assets/slickpay-splash/benefit-admin.png';
import IMG_FEES from 'assets/slickpay-splash/benefit-fees.png';
import IMG_TIPS from 'assets/slickpay-splash/benefit-tips.png';
import IMG_HERO_BANNER from 'assets/slickpay-splash/hero-banner-img.png';
import IMG_LOGO from 'assets/slickpay-splash/logoes/skick-pay-white.png';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonId, selectCurrentSalonStylists } from 'selectors/currentSalon.selectors';
import logger from 'utils/logger.util';
import { getActiveStylists } from 'utils/salons/getActiveStylists.util';
import C from 'utils/values/constant.util';

import PaymentTabs from './PaymentTabs.component';

import CN from './NoReaderLayout.module.scss';

const L = logger('NoReaderLayout');

const LINK_CARD = 'https://gettingslick.typeform.com/SlickPayTerms?utm_source=splash';

// NOTE: these values will not be dynamic
const HOURS_SAVED = 10;
const TIP_PER_MONTH = 1700;
const FEES_PER_YEAR = 700;

const translateDataTopBanner = (t) =>
  C([
    t('slickPay.noReader.topBannerBullets.payouts'),
    t('slickPay.noReader.topBannerBullets.hours'),
    t('slickPay.noReader.topBannerBullets.tips'),
  ]);

const NoReaderLayout = ({ stylists, salonId, fetchStylistsBySalon }) => {
  const { val: isTabsRendered, tip: toggle } = useTip(false);
  const { t } = useTranslation();

  const activeStylists = getActiveStylists(stylists);
  const isOneStylist = activeStylists.length <= 1;

  const DATA_TOP_BANNER = translateDataTopBanner(t);

  useEffect(
    //
    () => void L.unignore('useEffect()', 'fetchStylistsBySalon:', fetchStylistsBySalon(salonId)),
    [fetchStylistsBySalon, salonId],
  );

  return (
    <div data-bem="NoReaderLayout" className={CN.component}>
      <div className={CN.wrap}>
        <Banner data-bem="NoReaderLayout__Banner-header">
          <div className={CN.heroBanner}>
            <div className={CN.bannerHeaderContainer}>
              <img src={IMG_LOGO} alt="Slick logo" className={CN.logo} />
              <div className={CN.topBannerTitle}>{t('slickPay.noReader.topBannerTitle')}</div>
              <Bullets data={DATA_TOP_BANNER} variantInvert checkmarkWhite />
              <div className={CN.bannerHeaderLink}>
                <ExternalLink link={LINK_CARD}>
                  <span className={CN.bannerButton}>{t('slickPay.noReader.topBannerButton')}</span>
                </ExternalLink>
              </div>
            </div>

            <div className={CN.heroImgContainer}>
              <img src={IMG_HERO_BANNER} alt="Slick logo" className={CN.heroImg} />
            </div>
          </div>
        </Banner>

        {/* Benefits section */}
        <div data-bem="SettingSummary__benefit-title" className={CN.benefitsTitle}>
          {t('slickPay.noReader.benefits.title')}
        </div>
        <div data-bem="SettingSummary__benefit-container" className={CN.benefitsContainer}>
          {/* Free staff tips */}
          <div data-bem="SettingSummary__benefit-tips" className={CN.benefitItem}>
            <div className={CN.textAndImgContainer}>
              <div className={CN.benefitName}> {t('slickPay.noReader.benefits.freeTips.name')}</div>
              <img data-bem="SettingSummary__img--reminders" src={IMG_TIPS} className={CN.imageBenefit} alt="" />
            </div>
            <div className={CN.boxContent}>
              <div className={CN.boxKey}>
                <div className={CN.boxTitle}> {t('slickPay.noReader.benefits.freeTips.boxTitle')}</div>
                <div className={CN.boxValue}>
                  <CurrencySign />
                  {TIP_PER_MONTH}
                </div>
              </div>

              <div className={CN.benefitText}>
                {t('slickPay.noReader.benefits.freeTips.storyLine1')}
                <Space>
                  <div className={CN.bold}>
                    <CurrencySign />
                    {t('slickPay.noReader.benefits.freeTips.storyHighlight')}
                  </div>
                </Space>
              </div>

              <div className={CN.benefitText}>{t('slickPay.noReader.benefits.freeTips.storyLine2')}</div>
            </div>
          </div>

          {/* Low card fees */}
          <div data-bem="SettingSummary__benefit-fees" className={CN.benefitItem}>
            <div className={CN.textAndImgContainer}>
              <div className={CN.benefitName}> {t('slickPay.noReader.benefits.lowFees.name')}</div>
              <img data-bem="SettingSummary__img--marketing" src={IMG_FEES} className={CN.imageBenefit} alt="" />
            </div>
            <div className={CN.boxContent}>
              <div className={CN.boxKey}>
                <div className={CN.boxTitle}> {t('slickPay.noReader.benefits.lowFees.boxTitle')}</div>
                <div className={CN.boxValue}>
                  <CurrencySign />
                  {FEES_PER_YEAR}
                </div>
              </div>
              <div className={CN.benefitText}>
                {t('slickPay.noReader.benefits.lowFees.storyLine1')}
                <Space>
                  <div className={CN.bold}>
                    <CurrencySign />
                    {t('slickPay.noReader.benefits.lowFees.monthlyAmount')}
                    <CurrencySign />
                    {t('slickPay.noReader.benefits.lowFees.yearlyAmount')}
                  </div>
                </Space>
                {t('slickPay.noReader.benefits.lowFees.storyLine2')}
              </div>

              <div className={CN.benefitText}>
                {t('slickPay.noReader.benefits.lowFees.ratesLine1')}
                <Space>
                  <div className={CN.bold}>{t('slickPay.noReader.benefits.lowFees.ratesHighlight')}</div>
                </Space>
                {t('slickPay.noReader.benefits.lowFees.ratesLine2')}
                <Space>
                  <CurrencySign />
                  {t('slickPay.noReader.benefits.lowFees.ratesLine3')}
                </Space>
              </div>
            </div>
          </div>

          {/* No more admin */}
          <div data-bem="SettingSummary__benefit-admin" className={CN.benefitItem}>
            <div className={CN.textAndImgContainer}>
              <div className={CN.benefitName}> {t('slickPay.noReader.benefits.noAdmin.name')}</div>
              <img data-bem="SettingSummary__img--booking" src={IMG_ADMIN} className={CN.imageBenefit} alt="" />
            </div>
            <div className={CN.boxContent}>
              <div className={CN.boxKey}>
                <div className={CN.boxTitle}> {t('slickPay.noReader.benefits.noAdmin.boxTitle')}</div>
                <div className={CN.boxValue}>{HOURS_SAVED}</div>
              </div>

              <div className={CN.benefitText}>
                {t('slickPay.noReader.benefits.noAdmin.storyLine1')}
                <Space>
                  <div className={CN.bold}>{t('slickPay.noReader.benefits.noAdmin.storyHighlight')}</div>
                </Space>
                {t('slickPay.noReader.benefits.noAdmin.storyLine2')}
              </div>

              <div className={CN.benefitText}>
                {t('slickPay.noReader.benefits.noAdmin.storyLine3')}
                <Space>
                  <div className={CN.bold}>{t('slickPay.noReader.benefits.noAdmin.storyHighlight2')}</div>
                </Space>
                {t('slickPay.noReader.benefits.noAdmin.storyLine4')}
              </div>
            </div>
          </div>
        </div>

        {!isOneStylist && (
          <div>
            <div className={CN.tabsSpace}>
              <PaymentTabs />
            </div>
            <div className={CN.panelSpace}>
              <AlertPanel tag="NoReaderLayout__AlertPanel-many-stylists" variant="slick">
                <div className={CN.alertContainer}>
                  <Icon tag="PaymentTabs__users" variant="buildingPurple" width="xl" />
                  <div className={CN.alertText}>
                    <span className={CN.extraBold}>{t('slickPay.noReader.mixMatch')} </span>
                    {t('slickPay.noReader.paymentMessage')}
                  </div>
                </div>
              </AlertPanel>
            </div>
          </div>
        )}
        {isOneStylist ? <div className={CN.gapSm} /> : <div className={CN.gapMd} />}

        <ImageCardGroup />

        <Banner data-bem="NoReaderLayout__Banner-reader">
          <div className={CN.bannerReaderContainer}>
            <div className={CN.readerBannerTitle}>{t('slickPay.noReader.readerBannerTitle')}</div>
            <div className={CN.readerBannerSubTitle}>{t('slickPay.noReader.readerBannerSubtitle')}</div>
            <div className={CN.readerBannerLink}>
              <ExternalLink link={LINK_CARD}>
                <span className={CN.bannerButton}>{t('slickPay.noReader.readerBannerButton')}</span>
              </ExternalLink>
            </div>
          </div>
        </Banner>
      </div>

      <div className={CN.wrap}>
        {isOneStylist && (
          <div className={CN.gapLg}>
            <AlertPanel tag="NoReaderLayout__AlertPanel-one-stylist" variant="slick">
              <div className={CN.flex}>
                <div className={CN.alertContainer}>
                  <Icon tag="PaymentTabs__users" variant="users" width="xl" />
                  <div className={CN.alertText}>
                    {t('slickPay.noReader.alertPart1')}
                    <span className={CN.extraBold}>{t('slickPay.noReader.alertPart2')}</span>
                  </div>
                </div>
                <Button onClick={toggle} variant="primaryForce">
                  {t('slickPay.noReader.optionsButton')}
                </Button>
              </div>
            </AlertPanel>

            {isTabsRendered && (
              <div className={CN.margLg}>
                <PaymentTabs />
              </div>
            )}
          </div>
        )}
      </div>

      <div className={CN.wrap}>
        <FaqTips />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  stylists: selectCurrentSalonStylists(state),
  salonId: selectCurrentSalonId(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ fetchStylistsBySalon }, dispatch);

const NoReaderLayoutHoc = connect(mapStateToProps, mapDispatchToProps)(NoReaderLayout);

export default NoReaderLayoutHoc;
